import type { ReactElement } from 'react';
import { Fragment, useEffect, useRef, useState } from 'react';

import { capitalize } from 'utils/strings';
import { useCart } from 'contexts/Cart';
import useCurrentUser from 'hooks/useCurrentUser';
import { FeatureFlag, HeapCustomEvent } from 'config';
import HeapAnalytics from 'services/HeapAnalytics';
import useIsInViewport from 'hooks/useIsInViewPort';

import MealTypes from 'models/MealTypes';

import MealTypeRecipes from './MealTypeRecipes';
import ThanksgivingBanner from '../../UpcomingMenus/banners/ThanksgivingBanner';
import ThanksgivingLTOCarousel from './ThanksgivingLTOCarousel';

type Props = {
  filter: string;
  subFilter: string;
  preferenceFilters: string[];
  setFilteredResultCount: (count: number) => void;
};

function Recipes({
  filter,
  subFilter,
  preferenceFilters,
  setFilteredResultCount,
}: Props): ReactElement {
  const { cart } = useCart();
  const { currentUser } = useCurrentUser();
  const isBundlesEnabled = currentUser.hasFeatureEnabled(FeatureFlag.BUNDLES);
  const isRteDaypartFiltersEnabled = currentUser.hasFeatureEnabled(FeatureFlag.RTE_DAYPART_FILTERS);
  const isLTOThanksgivingEnabled = currentUser.hasFeatureEnabled(FeatureFlag.LTO_THANKSGIVING);
  const { skus_added_outside_widget: skusAddedOutsideWidget } = cart;
  const isThanksgivingMenu = cart.is_thanksgiving_menu;

  const filteredList = cart.filteredProductsByMealType(
    filter, subFilter, preferenceFilters, isRteDaypartFiltersEnabled,
  );

  // We want to sort the menu items by the display_order field
  Object.keys(filteredList)
    .map((mealType) => {
      const isMealKit = mealType === 'breakfast' || mealType === 'lunch' || mealType === 'dinner';
      if (isMealKit) {
        // @ts-expect-error TODO: fix null safety
        return filteredList[mealType].sort((a, b) => {
          if (a.items[0].display_order > b.items[0].display_order) {
            return 1;
          }
          if (b.items[0].display_order > a.items[0].display_order) {
            return -1;
          }
          return 0;
        });
      }
      return filteredList[mealType];
    });

  if (!isBundlesEnabled) {
    delete filteredList.bundle;
  }

  const { hasFavoritesEnabled } = currentUser;
  const refPlantrySection = useRef(null);
  // @ts-expect-error TODO: fix null safety
  const isPlantryInViewport = useIsInViewport(refPlantrySection);

  useEffect(() => {
    if (isPlantryInViewport) {
      HeapAnalytics.track(HeapCustomEvent.PLANTRY_LIST_VIEWED, {
        user_status: 'logged_in',
        page_viewed: `/carts/${cart.id}/edit`,
      });
    }
  }, [isPlantryInViewport, cart]);

  const [packageProductsInCart, setPackageProductsInCart] = useState([]);
  const [lastAddedItem, setLastAddedItem] = useState(null);
  const [pageLoaded, setPageLoaded] = useState(false);
  const packageProductMenuItems = cart.menu_items.filter((item) => {
    const hasMatchingSku = cart.package_products.some(
      (packageProduct) => packageProduct.sku_id === item.sku.id,
    );

    return hasMatchingSku;
  });
  const allItemsInCart = packageProductMenuItems.filter((item) => item.quantity === 0).length === 0;

  // Set the first package product item as LatestAddedItem to know where to place the widget
  useEffect(() => {
    const itemsInCart = packageProductMenuItems.filter((item) => item.quantity > 0);
    const areEqual = itemsInCart.length === packageProductsInCart.length && itemsInCart
    // @ts-expect-error TODO: fix null safety
      .every((product, index) => product.sku.id === packageProductsInCart[index].sku.id);
    if (pageLoaded) {
      const mostRecentItemAdded = itemsInCart
        .filter((item) => !packageProductsInCart
        // @ts-expect-error TODO: fix null safety
          .some((cartItem) => cartItem.sku.id === item.sku.id));
      if (mostRecentItemAdded.length > 0) {
        const itemAddedByRecipeTile = skusAddedOutsideWidget
        // @ts-expect-error TODO: fix null safety
          .map((item) => item.sku.id)
          .includes(mostRecentItemAdded.map((item) => item.sku.id)[0]);
        if (lastAddedItem === null && !allItemsInCart && itemAddedByRecipeTile) {
          // @ts-expect-error TODO: fix null safety
          setLastAddedItem(mostRecentItemAdded[mostRecentItemAdded.length - 1]);
        }
      }
    }

    if (pageLoaded || packageProductsInCart.length !== itemsInCart.length) {
      if (!areEqual) {
        // @ts-expect-error TODO: fix null safety
        setPackageProductsInCart(itemsInCart);
      }
    }
    setPageLoaded(true);
  }, [packageProductMenuItems, packageProductsInCart, pageLoaded,
    allItemsInCart, lastAddedItem, skusAddedOutsideWidget]);

  useEffect(() => {
    const filteredResultCount = Object.keys(filteredList)
      .reduce(
        (count, mealType) => count + filteredList[mealType].length, 0,
      );
    setFilteredResultCount(filteredResultCount);
  }, [filteredList, setFilteredResultCount]);

  return (
    <main id="content" aria-label="Main content">
      <div className="l-container">
        <div className="c-meal-management-recipes-container">
          {isLTOThanksgivingEnabled && isThanksgivingMenu && (
            <div className="c-meal-management-recipes-LTO__container">
              <ThanksgivingBanner isEditCartPage hasCart />
              <ThanksgivingLTOCarousel />
            </div>
          )}
          {Object.keys(filteredList)
            .map((mealType, index) => (
              <Fragment key={mealType}>
                <div
                  id={MealTypes[mealType].narrativeKey}
                  className="c-meal-management-recipes-container__title"
                  style={isLTOThanksgivingEnabled ? { marginTop: '40px' } : {}}
                >
                  <h2
                    className="c-heading c-heading--brand-4"
                    ref={mealType === 'snack' || mealType === 'grocery' ? refPlantrySection : null}
                  >
                    {capitalize(MealTypes[mealType].narrative)}
                    {mealType === 'snack' && (
                      <sup> ™</sup>
                    )}
                  </h2>

                  {MealTypes[mealType].description && (
                    <div
                      className={[
                        'u-text',
                        'u-text--md',
                        'd-none',
                        'd-sm-block',
                      ].join(' ')}
                    >
                      {MealTypes[mealType].description}
                    </div>
                  )}
                </div>
                <MealTypeRecipes
                  productsByMealType={filteredList[mealType]}
                  hasFavoritesEnabled={hasFavoritesEnabled}
                    // @ts-expect-error TODO: fix null safety
                  lastAddedItem={lastAddedItem}
                  mealTypeIndex={index}
                />
              </Fragment>
            ))}
        </div>
      </div>

      <form className="heap-snapshot d-none">
        <input id="heap-menu_id" type="text" value={cart.menu_id} readOnly />
      </form>
    </main>
  );
}

export default Recipes;
